import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Radio,
  Card,
  Alert,
  Space,
  Typography,
  Row,
  Col,
  Divider
} from 'antd';
import { GoogleOutlined, FacebookOutlined, BarChartOutlined, DatabaseOutlined } from '@ant-design/icons';
import { auditAPI } from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

// Date range options (same as in AuditSettings)
const GOOGLE_ADS_DATE_RANGES = [
  { value: 'TODAY', label: 'Bugün' },
  { value: 'YESTERDAY', label: 'Dün' },
  { value: 'LAST_7_DAYS', label: 'Son 7 Gün' },
  { value: 'LAST_14_DAYS', label: 'Son 14 Gün' },
  { value: 'LAST_30_DAYS', label: 'Son 30 Gün' },
  { value: 'LAST_BUSINESS_WEEK', label: 'Geçen İş Haftası' },
  { value: 'THIS_MONTH', label: 'Bu Ay' },
  { value: 'LAST_MONTH', label: 'Geçen Ay' },
  { value: 'THIS_WEEK_SUN_TODAY', label: 'Bu Hafta (Pazar-Bugün)' },
  { value: 'THIS_WEEK_MON_TODAY', label: 'Bu Hafta (Pazartesi-Bugün)' },
  { value: 'LAST_WEEK_SUN_SAT', label: 'Geçen Hafta (Pazar-Cumartesi)' },
  { value: 'LAST_WEEK_MON_SUN', label: 'Geçen Hafta (Pazartesi-Pazar)' }
];

const GOOGLE_ANALYTICS_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7_days', label: 'Son 7 Gün' },
  { value: 'last_14_days', label: 'Son 14 Gün' },
  { value: 'last_30_days', label: 'Son 30 Gün' },
  { value: 'last_90_days', label: 'Son 90 Gün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const FACEBOOK_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_3d', label: 'Son 3 Gün' },
  { value: 'last_7d', label: 'Son 7 Gün' },
  { value: 'last_14d', label: 'Son 14 Gün' },
  { value: 'last_28d', label: 'Son 28 Gün' },
  { value: 'last_30d', label: 'Son 30 Gün' },
  { value: 'last_90d', label: 'Son 90 Gün' },
  { value: 'this_week_mon_today', label: 'Bu Hafta (Pazartesi-Bugün)' },
  { value: 'this_week_sun_today', label: 'Bu Hafta (Pazar-Bugün)' },
  { value: 'last_week_mon_sun', label: 'Geçen Hafta (Pazartesi-Pazar)' },
  { value: 'last_week_sun_sat', label: 'Geçen Hafta (Pazar-Cumartesi)' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' },
  { value: 'this_quarter', label: 'Bu Çeyrek' },
  { value: 'last_quarter', label: 'Geçen Çeyrek' },
  { value: 'this_year', label: 'Bu Yıl' },
  { value: 'last_year', label: 'Geçen Yıl' }
];

const CUSTOM_DATA_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const BrandAuditModal = ({
  visible,
  brand,
  capabilities,
  onCancel,
  onSuccess,
  onError,
  setLoading
}) => {
  const [form] = Form.useForm();
  const [selectedAuditType, setSelectedAuditType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && capabilities) {
      // Auto-select audit type if only one is available
      const availableTypes = [];
      if (capabilities.hasGoogleAds) availableTypes.push('google_ads');
      if (capabilities.hasFacebookAds) availableTypes.push('facebook');
      if (capabilities.hasGoogleAnalytics) availableTypes.push('google_analytics');
      if (capabilities.hasCustomData) availableTypes.push('custom_data');
      
      if (availableTypes.length === 1) {
        setSelectedAuditType(availableTypes[0]);
        form.setFieldsValue({ auditType: availableTypes[0] });
      }
    }
  }, [visible, capabilities, form]);

  const handleAuditTypeChange = (value) => {
    setSelectedAuditType(value);
    // Reset form fields when audit type changes
    form.setFieldsValue({
      auditType: value,
      dateRange: undefined,
      model: undefined,
      prompt: undefined
    });
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      setLoading(true);

      const auditData = {
        auditType: values.auditType,
        ...(values.dateRange && { dateRange: values.dateRange }),
        ...(values.model && { model: values.model }),
        ...(values.prompt && { prompt: values.prompt })
      };

      const response = await auditAPI.triggerBrandAudit(brand.id, auditData);
      
      if (response.data.success) {
        onSuccess(response.data.audit);
        form.resetFields();
        setSelectedAuditType(null);
      } else {
        throw new Error(response.data.error || 'Audit başlatılamadı');
      }
    } catch (error) {
      onError(error.response?.data?.error || error);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedAuditType(null);
    onCancel();
  };

  const getDateRangeOptions = () => {
    if (selectedAuditType === 'google_ads') {
      return GOOGLE_ADS_DATE_RANGES;
    }
    if (selectedAuditType === 'facebook') {
      return FACEBOOK_DATE_RANGES;
    }
    if (selectedAuditType === 'google_analytics') {
      return GOOGLE_ANALYTICS_DATE_RANGES;
    }
    if (selectedAuditType === 'custom_data') {
      return CUSTOM_DATA_DATE_RANGES;
    }
    return [];
  };

  const getAccountInfo = () => {
    if (!selectedAuditType || !capabilities) return null;

    if (selectedAuditType === 'google_ads' && capabilities.googleAdsCodes?.length > 0) {
      const code = capabilities.googleAdsCodes[0];
      return {
        type: 'Google Ads',
        icon: <GoogleOutlined style={{ color: '#4285F4' }} />,
        accountId: code.parameters?.customerId,
        name: code.name
      };
    }

    if (selectedAuditType === 'facebook' && capabilities.facebookAdsCodes?.length > 0) {
      const code = capabilities.facebookAdsCodes[0];
      return {
        type: 'Facebook Ads',
        icon: <FacebookOutlined style={{ color: '#1877F2' }} />,
        accountId: code.parameters?.adAccountId,
        name: code.name
      };
    }

    if (selectedAuditType === 'google_analytics' && capabilities.googleAnalyticsCodes?.length > 0) {
      const code = capabilities.googleAnalyticsCodes[0];
      const parameters = JSON.parse(code.value || '{}');
      return {
        type: 'Google Analytics',
        icon: <BarChartOutlined style={{ color: '#FF9800' }} />,
        accountId: parameters.id,
        name: code.name || `GA4 Property ${parameters.id}`
      };
    }

    if (selectedAuditType === 'custom_data') {
      return {
        type: 'ReportsUp Analizi',
        icon: <DatabaseOutlined style={{ color: '#722ED1' }} />,
        accountId: `brand_${brand?.id}`,
        name: `${brand?.name} Marka Verileri`
      };
    }

    return null;
  };

  const accountInfo = getAccountInfo();

  return (
    <Modal
      title={`${brand?.name} - Audit Yap`}
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          İptal
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          disabled={!selectedAuditType}
          onClick={() => form.submit()}
        >
          Audit Başlat
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Audit Type Selection */}
        <Form.Item
          label="Audit Türü"
          name="auditType"
          rules={[{ required: true, message: 'Audit türü seçin' }]}
        >
          <Radio.Group onChange={(e) => handleAuditTypeChange(e.target.value)}>
            {capabilities?.hasGoogleAds && (
              <Radio.Button value="google_ads">
                <Space>
                  <GoogleOutlined style={{ color: '#4285F4' }} />
                  Google Ads
                </Space>
              </Radio.Button>
            )}
            {capabilities?.hasFacebookAds && (
              <Radio.Button value="facebook">
                <Space>
                  <FacebookOutlined style={{ color: '#1877F2' }} />
                  Facebook Ads
                </Space>
              </Radio.Button>
            )}
            {capabilities?.hasGoogleAnalytics && (
              <Radio.Button value="google_analytics">
                <Space>
                  <BarChartOutlined style={{ color: '#FF9800' }} />
                  Google Analytics
                </Space>
              </Radio.Button>
            )}
            {capabilities?.hasCustomData && (
              <Radio.Button value="custom_data">
                <Space>
                  <DatabaseOutlined style={{ color: '#722ED1' }} />
                  ReportsUp Analizi
                </Space>
              </Radio.Button>
            )}
          </Radio.Group>
        </Form.Item>

        {/* Account Information */}
        {accountInfo && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row align="middle">
              <Col span={2}>
                {accountInfo.icon}
              </Col>
              <Col span={22}>
                <Text strong>{accountInfo.name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {accountInfo.type}: {accountInfo.accountId}
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {selectedAuditType && (
          <>
            <Divider />
            
            {/* Optional Overrides */}
            <Title level={5}>İsteğe Bağlı Ayarlar</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Tarih Aralığı"
                  name="dateRange"
                  help="Boş bırakılırsa varsayılan ayar kullanılır"
                >
                  <Select placeholder="Tarih aralığı seçin" allowClear>
                    {getDateRangeOptions().map(range => (
                      <Option key={range.value} value={range.value}>
                        {range.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Ai Model"
                  name="model"
                  help="Boş bırakılırsa varsayılan model kullanılır"
                >
                  <Input placeholder="google/gemini-2.0-flash-exp" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Özel Prompt"
              name="prompt"
              help="Boş bırakılırsa varsayılan prompt kullanılır"
            >
              <TextArea 
                rows={4}
                placeholder={selectedAuditType === 'custom_data' 
                  ? 'Marka verilerini analiz et ve performans önerilerini ver...' 
                  : `${accountInfo?.type} performansını analiz et ve optimizasyon önerileri sun...`}
              />
            </Form.Item>

            <Alert
              message="Audit süreci 1-3 dakika sürebilir"
              description="Audit tamamlandığında sonuçları Audit Geçmişi sayfasından görüntüleyebilirsiniz."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default BrandAuditModal;